/* global axios */
import ApiClient from './ApiClient';

// Passed to axios as `params`, which encodes each value. The old hand-built query string did not,
// so a `+` in an email became a space and an `&` cut the search term.
export const buildContactParams = (page, sortAttr, label, search) => ({
  include_contact_inboxes: false,
  page,
  sort: sortAttr,
  ...(search ? { q: search } : {}),
  ...(label ? { labels: [label] } : {}),
});

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    return axios.get(this.url, {
      params: buildContactParams(page, sortAttr, label, ''),
    });
  }

  show(id) {
    return axios.get(`${this.url}/${id}?include_contact_inboxes=false`);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  getConversations(contactId) {
    return axios.get(`${this.url}/${contactId}/conversations`);
  }

  // Dedupes on email -> phone_number -> identifier server side
  // (Contacts::CreationService), unlike create() which always inserts.
  findOrCreate(payload) {
    return axios.post(`${this.url}/find_or_create`, payload);
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '') {
    return axios.get(`${this.url}/search`, {
      params: buildContactParams(page, sortAttr, label, search),
    });
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    return axios.post(`${this.url}/filter`, queryPayload, {
      params: buildContactParams(page, sortAttr),
    });
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  blockDomain(contactId) {
    return axios.post(`${this.url}/${contactId}/block_domain`);
  }

  unblockDomain(contactId) {
    return axios.post(`${this.url}/${contactId}/unblock_domain`);
  }

  getCustomerInfos(contactId, conversationId = null) {
    return axios.get(`${this.url}/${contactId}/customer_infos`, {
      params: { conversation_id: conversationId },
    });
  }
}

export default new ContactAPI();
